import { FC, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  selectIsUserDataInitialized,
  selectIsUserLoggedIn,
  selectShouldShowAppBanner,
  selectShouldShowOnboardingNewsletter,
  selectUserProfile,
  setShouldShowAppBanner,
} from '@/store/slices/user';

const persistAppBannerDismiss = async (): Promise<void> => {
  const postDismiss = () =>
    fetch('/api/account/app-banner/dismiss', {
      method: 'POST',
    });

  try {
    const response = await postDismiss();

    if (!response.ok) {
      await postDismiss();
    }
  } catch {
    try {
      await postDismiss();
    } catch {
      // Cookie remains as same-device fallback for signed-in users.
    }
  }
};

const hideVisibleBanner = (): void => {
  const banner = document.querySelector('.js_smartbanner');

  if (!banner) {
    return;
  }

  const html = document.documentElement;
  const originalMargin = html.getAttribute(
    'data-smartbanner-original-margin-top',
  );

  if (originalMargin !== null) {
    html.style.marginTop = `${originalMargin}px`;
    html.removeAttribute('data-smartbanner-original-margin-top');
  } else {
    html.style.marginTop = '';
  }

  banner.remove();
};

const SmartbannerGate: FC = () => {
  const dispatch = useDispatch();
  const isDataInitialized = useSelector(selectIsUserDataInitialized);
  const isAuthenticated = useSelector(selectIsUserLoggedIn);
  const shouldShowAppBanner = useSelector(selectShouldShowAppBanner);
  const shouldShowOnboarding = useSelector(
    selectShouldShowOnboardingNewsletter,
  );
  const profile = useSelector(selectUserProfile);
  const userSub = profile?.sub;
  const publishedRef = useRef(false);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const userSubRef = useRef(userSub);

  isAuthenticatedRef.current = isAuthenticated;
  userSubRef.current = userSub;

  useEffect(() => {
    if (!isDataInitialized) {
      return undefined;
    }

    if (shouldShowOnboarding) {
      hideVisibleBanner();
      publishedRef.current = false;

      return undefined;
    }

    publishedRef.current = false;

    const tryPublish = (): void => {
      const { smartbanner } = window;

      if (!smartbanner || publishedRef.current) {
        return;
      }

      if (isAuthenticated && !shouldShowAppBanner) {
        return;
      }

      const published = smartbanner.publish();

      if (published !== false) {
        publishedRef.current = true;
      }
    };

    const attemptPublish = (): void => {
      if (window.smartbanner) {
        tryPublish();
      }
    };

    attemptPublish();

    if (!window.smartbanner) {
      window.addEventListener('load', attemptPublish);
    }

    return () => {
      window.removeEventListener('load', attemptPublish);
    };
  }, [
    isAuthenticated,
    isDataInitialized,
    shouldShowAppBanner,
    shouldShowOnboarding,
  ]);

  useEffect(() => {
    const onView = (): void => {
      window.dataLayer?.push({
        event: 'app_banner_view',
        user_sub: userSubRef.current ?? null,
      });
    };

    const onExit = (): void => {
      window.dataLayer?.push({
        event: 'app_banner_dismiss',
        user_sub: userSubRef.current ?? null,
      });

      if (isAuthenticatedRef.current) {
        dispatch(setShouldShowAppBanner(false));
        persistAppBannerDismiss().catch(() => {
          // Cookie remains as same-device fallback for signed-in users.
        });
      }
    };

    const onClickout = (): void => {
      window.dataLayer?.push({
        event: 'app_banner_clickout',
        user_sub: userSubRef.current ?? null,
      });
    };

    document.addEventListener('smartbanner.view', onView);
    document.addEventListener('smartbanner.exit', onExit);
    document.addEventListener('smartbanner.clickout', onClickout);

    return () => {
      document.removeEventListener('smartbanner.view', onView);
      document.removeEventListener('smartbanner.exit', onExit);
      document.removeEventListener('smartbanner.clickout', onClickout);
    };
  }, [dispatch]);

  return null;
};

export default SmartbannerGate;
